<template>
  <FormBox :headline="handleFormBoxHeadline" :subline="handleFormBoxSubline">
    <form :class="classes()" slot="form" @submit.prevent="handleSubmit">
      <Swiper
        :class="`${el}__swiper`"
        ref="swiper"
        @swiper="onSwiper"
        :allow-touch-move="false"
        :auto-height="true"
        :space-between="0"
        :prevent-clicks="false"
      >
        <SwiperSlide
          :class="{
            [`${el}__swiperSlide`]: true,
            [`${el}__swiperSlide--active`]: activeIndex === 0,
          }"
        >
          <template v-if="activeIndex === 0">
            <div :class="`${el}__radioButtons`">
              <BaseRadio
                id="salutation-w"
                name="gender"
                :label="$t('forms.label.mrs')"
                :checked="formFields.gender.value === 2"
                @input="setGender(2)"
                :class="`${el}__radioButton`"
                tabindex="0"
                @keyup.enter.native="setGender(2)"
                @keyup.space.native="setGender(2)"
                role="radio"
                :aria-checked="formFields.gender.value === 2"
              />
              <BaseRadio
                id="salutation-m"
                name="gender"
                :label="$t('forms.label.mr')"
                :checked="formFields.gender.value === 1"
                @input="setGender(1)"
                :class="`${el}__radioButton`"
                tabindex="0"
                @keyup.enter.native="setGender(1)"
                @keyup.space.native="setGender(1)"
                role="radio"
                :aria-checked="formFields.gender.value === 1"
              />
            </div>
            <Copy
              v-if="formFields.gender.error"
              element="div"
              size="s"
              staticClass="u-margin__top--1 u-margin__bottom--3"
              color="supportDanger"
            >
              {{ $t('auth.register.form.error.gender.required') }}
            </Copy>

            <div :class="`${el}__inputRow u-margin__top--3`">
              <BaseInput
                label="Vorname"
                type="text"
                name="firstname"
                v-model.trim="formFields.firstname.value"
                :error="formFields.firstname.error"
                @input="clearError(formFields.firstname)"
                clearable
              />
            </div>
            <div :class="`${el}__inputRow`">
              <BaseInput
                label="Nachname"
                type="text"
                name="lastname"
                v-model.trim="formFields.lastname.value"
                :error="formFields.lastname.error"
                @input="clearError(formFields.lastname)"
                clearable
              />
            </div>
            <div :class="`${el}__inputRow`">
              <BaseInput
                label="E-Mail"
                :type="$matchMedia.l ? 'text' : 'email'"
                name="email"
                v-model.trim="formFields.email.value"
                :error="formFields.email.error"
                @input="clearError(formFields.email)"
                clearable
              />
            </div>
            <div :class="`${el}__inputRow`">
              <BaseInput
                label="Passwort"
                type="password"
                name="password"
                v-model="formFields.password.value"
                :error="formFields.password.error"
                @input="clearError(formFields.password)"
                clearable
                @iconClicked="trackClick('-Step-1_click-Passwort Anzeigen', 'element')"
              />
            </div>
            <PasswordStrength
              :class="`${el}__passwordStrength`"
              :password="formFields.password.value"
            />
          </template>
        </SwiperSlide>
        <SwiperSlide
          :class="{
            [`${el}__swiperSlide`]: true,
            [`${el}__swiperSlide--active`]: activeIndex === 1,
          }"
        >
          <template v-if="activeIndex === 1">
            <div :class="`${el}__checkboxRow`">
              <BaseCheckbox
                name="privacyStatement"
                v-model="formFields.privacyStatement.value"
                :error="formFields.privacyStatement.error"
                @input="clearError(formFields.privacyStatement)"
              >
                <span slot="label">{{ $t('auth.register.form.checkbox1') }}</span>
              </BaseCheckbox>
            </div>
            <div :class="`${el}__checkboxRow`">
              <BaseCheckbox
                name="newsletter"
                v-model="formFields.newsletter.value"
                :error="formFields.newsletter.error"
                @input="clearError(formFields.newsletter)"
              >
                <span slot="label">{{ $t('auth.register.form.checkbox2') }}</span>
              </BaseCheckbox>
            </div>
            <div :class="`${el}__checkboxRow`">
              <BaseCheckbox
                name="privacyProxy"
                v-model="formFields.privacyProxy.value"
                :error="formFields.privacyProxy.error"
                @input="clearError(formFields.privacyProxy)"
              >
                <span slot="label">{{ $t('auth.register.form.checkbox3') }}</span>
              </BaseCheckbox>
            </div>
            <Copy element="div" size="s">
              <i>
                <strong v-html="$t('auth.register.form.notice.name')" />
              </i>
              <i v-html="$t('auth.register.form.notice.line1')" />
              <i v-html="$t('auth.register.form.notice.line2')" />
              <i
                v-html="
                  $t('auth.register.form.notice.line3', {
                    link:
                      `<a href='` +
                      ROUTE_PRIVACY +
                      `' target='_blank' data-tracking='-Step2_link-` +
                      ROUTE_PRIVACY +
                      `'>Datenschutzhinweise</a>`,
                  })
                "
              />
              <i
                v-html="
                  $t('auth.register.form.notice.line4', {
                    link: `<a href='https://www.pflege.de/datenschutz/' target='_blank' data-tracking='-Step2_link-www.pflege.de/datenschutz'>Datenschutzhinweise</a>`,
                  })
                "
              />
              <i v-html="$t('auth.register.form.notice.line5')" />
            </Copy>
          </template>
        </SwiperSlide>
        <SwiperSlide
          :class="{
            [`${el}__swiperSlide`]: true,
            [`${el}__swiperSlide--active`]: activeIndex === 2,
          }"
        >
          <div :class="`${el}__done`">
            <Icon name="mail-send-fill" color="supportSuccess" :class="`${el}__doneIcon`" />
            <Headline size="s" responsive centered noMargin>
              <span v-html="$t('auth.register.form.done.headline')" />
            </Headline>
            <Copy color="tertiaryLight" size="xl">{{ $t('auth.register.form.done.copy') }}</Copy>
            <Copy color="tertiaryLight" size="m" v-html="$t('auth.spam.copy')" />
          </div>
        </SwiperSlide>
      </Swiper>
      <Copy v-if="genericError" color="supportDanger">
        {{ $t('auth.register.form.error.generics') }}
      </Copy>
      <div v-if="activeIndex < 2" :class="`${el}__buttonRow`">
        <Button
          :class="`${el}__button`"
          :label="
            $t(activeIndex === 0 ? 'auth.register.form.button.next' : 'auth.register.form.button')
          "
          :loading="loading"
          submit
        />
      </div>
    </form>
  </FormBox>
</template>

<script>
import Vue from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import BaseInput from '@/objects/Forms/BaseInput';
import BaseCheckbox from '@/objects/Forms/BaseCheckbox';
import Button from '@/objects/Button';
import Copy from '@/objects/Copy';
import Icon from '@/objects/Icon';
import Headline from '@/objects/Headline';
import PasswordStrength from '@/components/PasswordStrength';
import FormBox from '@/components/FormBox';
import BaseRadio from '@/objects/Forms/BaseRadio';

import { emailPattern, passwordPattern, namePattern } from '@/helpers/patterns';
import { validateForm } from '@/helpers/formValidation';
import { ROUTE_LOGIN, ROUTE_PRIVACY } from '@/constants/routes';
import { ROUTE_API_SIGN_UP } from '@/constants/apiRoutes';
import { scrollTo } from '@/helpers/scroll';

import { mapFieldsAuth } from '@/store/mapFields';
import { createTrackingData } from '@/helpers/tracking/utils/helpers';
import { createValidationData } from '@/helpers/tracking/validationTracking';

import { trackBIUnregisteredUser } from '@/helpers/biTracking/biTracking';
export default {
  name: 'RegisterForm',
  components: {
    BaseRadio,
    BaseInput,
    BaseCheckbox,
    Button,
    Copy,
    Icon,
    Headline,
    PasswordStrength,
    Swiper,
    SwiperSlide,
    FormBox,
  },
  props: {},
  data() {
    return {
      ROUTE_LOGIN,
      ROUTE_PRIVACY,
      viewIdx: 0,
      activeIndex: 0,
      loading: false,
      swiperInstance: null,
      el: 'c-registerForm',
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
      genericError: false,
      formFields: {
        firstname: {
          value: '',
          error: null,
          required: this.$t('auth.register.form.error.firstname.required'),
          pattern: {
            value: namePattern,
            message: this.$t('auth.register.form.error.firstname.formatted'),
          },
        },
        lastname: {
          value: '',
          error: null,
          required: this.$t('auth.register.form.error.lastname.required'),
          pattern: {
            value: namePattern,
            message: this.$t('auth.register.form.error.lastname.formatted'),
          },
        },
        gender: {
          value: '',
          error: null,
          required: this.$t('auth.register.form.error.gender.required'),
        },
        email: {
          value: '',
          error: null,
          required: this.$t('auth.register.form.error.email.required'),
          pattern: {
            value: emailPattern,
            message: this.$t('auth.register.form.error.email.formatted'),
          },
        },
        password: {
          value: '',
          error: null,
          pattern: {
            value: passwordPattern,
            message: this.$t('auth.register.form.error.password.formatted'),
          },
        },
        privacyStatement: {
          value: 0,
          error: null,
          required: this.$t('auth.register.form.error.privacyStatement.required'),
        },
        privacyProxy: {
          value: 0,
          error: null,
          required: false,
        },
        newsletter: {
          value: 0,
          error: null,
          required: false,
        },
      },
    };
  },
  computed: {
    ...mapFieldsAuth(['isAuthenticated']),
    handleFormBoxHeadline: function () {
      if (this.activeIndex === 0) {
        return this.$t('auth.register.form.headline');
      } else if (this.activeIndex === 1) {
        return this.$t('auth.register.form.privacy.headline');
      } else {
        return '';
      }
    },
    handleFormBoxSubline: function () {
      if (this.activeIndex === 0) {
        return this.$t('auth.register.form.subline');
      } else if (this.activeIndex === 1) {
        return this.$t('auth.register.form.privacy.subheadline');
      } else {
        return '';
      }
    },
  },

  updated() {
    const links = [...document.querySelectorAll('[data-tracking]')];
    links.map((link) => {
      link.addEventListener('click', () => {
        this.trackClick(link.dataset.tracking, 'link');
      });
    });
  },

  beforeDestroy() {
    const links = [...document.querySelectorAll('[data-tracking]')];
    links.map((link) => {
      link.removeEventListener('click');
    });
  },

  methods: {
    onSwiper(swiper) {
      this.swiperInstance = swiper;
    },

    clearError(field) {
      field.error = null;
    },
    validate(elements, formFields, done) {
      const { isValid } = validateForm(elements, formFields);
      if (isValid) {
        done();
      } else {
        createValidationData(this.formFields);
        this.loading = false;
      }
    },
    handleSubmit(e) {
      this.loading = true;
      this.genericError = false;
      const { elements } = e.target;

      if (this.activeIndex === 1) {
        this.validate(
          elements,
          {
            privacyStatement: this.formFields.privacyStatement,
            privacyProxy: this.formFields.privacyProxy,
            newsletter: this.formFields.newsletter,
          },
          () => {
            this.register();
            this.trackClick('-Step-2_btn-' + this.$t('auth.register.form.button'), 'button');
          }
        );
      }

      if (this.activeIndex === 0) {
        const isGenderValid = this.validateGender();
        this.validate(
          elements,
          {
            firstname: this.formFields.firstname,
            lastname: this.formFields.lastname,
            email: this.formFields.email,
            password: this.formFields.password,
          },
          () => {
            setTimeout(() => {
              if (isGenderValid) {
                this.slideNext();
              }
              this.trackClick(
                '-Step-1-_btn-' + this.$t('auth.register.form.button.next'),
                'button'
              );
              this.loading = false;
            }, 200);
          }
        );
      }
    },
    slideNext() {
      scrollTo(document.body, 0, 400);
      if (!this.$matchMedia.l) {
        setTimeout(() => {
          if (this.swiperInstance) {
            this.swiperInstance.slideNext();
            this.activeIndex = this.swiperInstance.activeIndex;
          }
        }, 400);
      } else {
        if (this.swiperInstance) {
          this.swiperInstance.slideNext();
          this.activeIndex = this.swiperInstance.activeIndex;
        }
      }
    },
    register() {
      const {
        firstname,
        lastname,
        gender,
        email,
        password,
        privacyStatement,
        privacyProxy,
        newsletter,
      } = this.formFields;
      const formData = new FormData();
      const user = {
        username: email,
        first_name: firstname,
        last_name: lastname,
        gender,
        password,
        privacyStatement: privacyStatement,
        privacyProxy: privacyProxy,
        newsletter: newsletter,
      };

      Object.keys(user).map((name) => {
        formData.append(`user[${name}]`, user[name].value);
      });

      Vue.axios
        .post(ROUTE_API_SIGN_UP, formData)
        .then((response) => {
          if (response.status === 200) {
            trackBIUnregisteredUser('account_created');
            this.slideNext();
            this.loading = false;
          }
        })
        .catch((err) => {
          const {
            data: { errors },
            status,
          } = err.response;

          if (status === 422) {
            let isErrorOnFirstStep = false;
            Object.keys(errors).map((error) => {
              const key = this.map(error);
              if (
                key === 'email' ||
                key === 'firstname' ||
                key === 'lastname' ||
                key === 'password' ||
                key === 'gender'
              ) {
                isErrorOnFirstStep = true;
              }

              const type = errors[error][0];
              this.formFields[key].error = this.$t(`auth.register.form.error.${key}.${type}`);
            });

            if (isErrorOnFirstStep && this.swiperInstance) {
              this.swiperInstance.slideTo(0);
              this.activeIndex = this.swiperInstance.activeIndex;
            }
            this.loading = false;
          } else {
            this.genericError = true;
            this.loading = false;
            if (this.swiperInstance) {
              this.swiperInstance.slideTo(0);
              this.activeIndex = this.swiperInstance.activeIndex;
            }
          }
        });
    },
    map(item) {
      return {
        'user.username': 'email',
        'user.first_name': 'firstname',
        'user.last_name': 'lastname',
        'user.gender': 'gender',
        'user.password': 'password',
        'user.privacyStatement': 'privacyStatement',
        'user.privacyProxy': 'privacyProxy',
        'user.newsletter': 'newsletter',
      }[item];
    },

    setGender(val) {
      this.formFields.gender.value = val;
      this.clearError(this.formFields.gender);
    },

    validateGender() {
      if (this.formFields.gender.value === '') {
        this.formFields.gender.error = this.formFields.gender.required;
        return false;
      } else {
        this.clearError(this.formFields.gender);
        return true;
      }
    },

    trackClick(labelText, element) {
      const product = 'Registrieren';
      const label = product + labelText;
      createTrackingData(product, label, element);
    },
  },
};
</script>
